import React, { useState, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Building2, UserPlus, Copy, CheckCircle2, ArrowLeft, ShieldCheck, Sparkles, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { CURRENCIES } from '../lib/currencies';
import SeedTrialCard from '../components/SeedTrialCard';

const INDUSTRIES = [
  'real_estate', 'property_management', 'facility_management',
  'residential_towers', 'commercial_buildings', 'mixed_use', 'retail_malls',
  'hospitality_hotels', 'healthcare', 'education', 'government',
  'industrial_warehouses', 'labor_camps', 'other',
];

const PLANS = [
  { key: 'basic', label: 'Basic — AED 199/mo' },
  { key: 'standard', label: 'Standard — AED 399/mo' },
  { key: 'pro', label: 'Pro — AED 799/mo' },
  { key: 'enterprise', label: 'Enterprise — Custom' },
];

/**
 * SuperAdminOnboardCompany — one-shot wizard for the platform owner (SUPERADMIN)
 * to create a Client Company + its Initial User Admin in a single transaction.
 * On success, the temp password is revealed ONCE for the operator to forward
 * to the client.
 */
export default function SuperAdminOnboardCompany() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const [form, setForm] = useState({
    // Company
    name: '', address: '', location: '', industry: 'real_estate',
    contact_person_name: '', contact_email: '', contact_phone: '',
    plan: 'standard', currency: 'AED', currency_symbol: 'AED',
    // Initial admin
    admin_name: '', admin_email: '', admin_phone: '', admin_role: 'FULL_ACCESS',
  });

  const setF = (patch) => setForm(f => ({ ...f, ...patch }));

  if (user && user.role !== 'SUPERADMIN') {
    return (
      <Layout>
        <div data-testid="sa-onboard-forbidden" className="max-w-2xl mx-auto py-16 text-center">
          <ShieldCheck className="w-14 h-14 mx-auto text-slate-300" />
          <h1 className="text-2xl font-bold mt-3">Super Admin only</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Onboarding a brand-new client company is a platform-owner action. Please contact your SKG Apps administrator.
          </p>
          <Button className="mt-6" variant="outline" onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
        </div>
      </Layout>
    );
  }

  const canNextStep1 = form.name.trim() && form.industry && form.location.trim() && form.plan;
  const canSubmit = form.admin_name.trim() && form.admin_email.trim() && form.admin_email.includes('@');

  const submit = async () => {
    if (!canSubmit) {
      toast.error('Please fill admin name and a valid email');
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/superadmin/onboard-company`, form, { withCredentials: true });
      setResult(r.data);
      setStep(3);
      toast.success('Company created — share the temp password now');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to onboard company');
    } finally {
      setSaving(false);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copied`),
      () => toast.error('Copy failed — select and copy manually')
    );
  };

  return (
    <Layout>
      <div data-testid="sa-onboard-page" className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-amber-600" />
              <span className="text-xs uppercase font-bold tracking-wide text-amber-700">Platform Admin</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Onboard New Client Company</h1>
            <p className="text-sm text-muted-foreground">Create the company + its Initial User Admin in one step.</p>
          </div>
          <Button variant="ghost" onClick={() => navigate('/superadmin')} data-testid="sa-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>

        {/* Phase 37.1 — One-click "Seed Demo Trial" for evaluators. */}
        <SeedTrialCard />

        {/* Stepper */}
        <div className="flex items-center gap-2 text-xs">
          {[
            { n: 1, label: 'Company' },
            { n: 2, label: 'Initial Admin' },
            { n: 3, label: 'Credentials' },
          ].map((s, i, arr) => (
            <React.Fragment key={s.n}>
              <div
                data-testid={`sa-step-${s.n}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full font-semibold ${
                  step === s.n ? 'bg-amber-500 text-slate-900' :
                  step > s.n ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                }`}
              >
                {step > s.n ? <CheckCircle2 className="w-3.5 h-3.5" /> : <span>{s.n}</span>}
                {s.label}
              </div>
              {i < arr.length - 1 && <div className={`h-px flex-1 ${step > s.n ? 'bg-emerald-300' : 'bg-slate-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {step === 1 && (
          <Card data-testid="sa-step1-card" className="p-6 space-y-4">
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="w-5 h-5 text-teal-600" />
              <h2 className="text-lg font-semibold text-foreground">Company details</h2>
            </div>

            <div>
              <Label>Company Name <span className="text-rose-600">*</span></Label>
              <Input data-testid="sa-company-name" value={form.name} onChange={(e) => setF({ name: e.target.value })} placeholder="Emirates Property Management LLC" />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>City / Emirate <span className="text-rose-600">*</span></Label>
                <Input data-testid="sa-company-location" value={form.location} onChange={(e) => setF({ location: e.target.value })} placeholder="Dubai" />
              </div>
              <div>
                <Label>Industry <span className="text-rose-600">*</span></Label>
                <Select value={form.industry} onValueChange={(v) => setF({ industry: v })}>
                  <SelectTrigger data-testid="sa-company-industry"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map(i => <SelectItem key={i} value={i}>{i.replace(/_/g, ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Address</Label>
              <Textarea rows={2} value={form.address} onChange={(e) => setF({ address: e.target.value })} placeholder="Office 1201, Business Bay Tower" />
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label>Contact Person</Label>
                <Input value={form.contact_person_name} onChange={(e) => setF({ contact_person_name: e.target.value })} placeholder="Defaults to Admin name" />
              </div>
              <div>
                <Label>Contact Email</Label>
                <Input value={form.contact_email} onChange={(e) => setF({ contact_email: e.target.value })} placeholder="Defaults to Admin email" />
              </div>
              <div>
                <Label>Contact Phone</Label>
                <Input value={form.contact_phone} onChange={(e) => setF({ contact_phone: e.target.value })} placeholder="+971..." />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Subscription Plan <span className="text-rose-600">*</span></Label>
                <Select value={form.plan} onValueChange={(v) => setF({ plan: v })}>
                  <SelectTrigger data-testid="sa-company-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLANS.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Currency</Label>
                <Select
                  value={form.currency}
                  onValueChange={(v) => {
                    const c = (CURRENCIES || []).find(x => x.code === v);
                    setF({ currency: v, currency_symbol: c?.symbol || v });
                  }}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(CURRENCIES || [{ code: 'AED', symbol: 'AED' }, { code: 'USD', symbol: '$' }]).slice(0, 20).map(c => (
                      <SelectItem key={c.code} value={c.code}>{c.code} — {c.symbol}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button
                data-testid="sa-next-step-1"
                disabled={!canNextStep1}
                onClick={() => {
                  // Prefill contact person / email from admin if empty when moving on
                  setStep(2);
                }}
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                Next: Initial Admin →
              </Button>
            </div>
          </Card>
        )}

        {step === 2 && (
          <Card data-testid="sa-step2-card" className="p-6 space-y-4">
            <div className="flex items-center gap-2 mb-1">
              <UserPlus className="w-5 h-5 text-amber-600" />
              <h2 className="text-lg font-semibold text-foreground">Initial User Admin</h2>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">
              This person will be the first user for <strong>{form.name || '(company)'}</strong>. They receive a one-time
              temporary password on the next screen and are forced to set a new one on first login.
            </p>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Full Name <span className="text-rose-600">*</span></Label>
                <Input data-testid="sa-admin-name" value={form.admin_name} onChange={(e) => setF({ admin_name: e.target.value })} placeholder="Rashid Al Falasi" />
              </div>
              <div>
                <Label>Email <span className="text-rose-600">*</span></Label>
                <Input data-testid="sa-admin-email" type="email" value={form.admin_email} onChange={(e) => setF({ admin_email: e.target.value.toLowerCase() })} placeholder="admin@companydomain.ae" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Phone (optional)</Label>
                <Input data-testid="sa-admin-phone" value={form.admin_phone} onChange={(e) => setF({ admin_phone: e.target.value })} placeholder="+971..." />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={form.admin_role} onValueChange={(v) => setF({ admin_role: v })}>
                  <SelectTrigger data-testid="sa-admin-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FULL_ACCESS">FULL_ACCESS (Owner)</SelectItem>
                    <SelectItem value="ADMIN">ADMIN</SelectItem>
                    <SelectItem value="CEO">CEO</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
              <strong>Heads up:</strong> the temporary password is generated by the server and shown to you ONCE on the
              next screen. Forward it via your secure channel (encrypted email, password manager share, or in person).
              We store only the bcrypt hash — nobody can recover the plaintext later.
            </div>

            <div className="flex justify-between pt-2">
              <Button data-testid="sa-back-step-2" variant="outline" onClick={() => setStep(1)}>← Back</Button>
              <Button data-testid="sa-submit-onboard" disabled={!canSubmit || saving} onClick={submit} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                {saving ? 'Creating…' : 'Create Company + Admin'}
              </Button>
            </div>
          </Card>
        )}

        {step === 3 && result && (
          <Card data-testid="sa-step3-card" className="p-6 space-y-4 border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              <h2 className="text-lg font-bold text-emerald-900">Onboarding complete</h2>
            </div>
            <p className="text-sm text-slate-700">
              <strong>{result.company?.name}</strong> is live. Share the credentials below with{' '}
              <strong>{result.initial_admin?.name}</strong> — the temp password will not be shown again.
            </p>

            <div className="bg-white rounded-lg p-4 border border-slate-200 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Login email</div>
                  <div data-testid="sa-result-email" className="font-mono text-sm text-slate-900 truncate">{result.initial_admin?.email}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => copy(result.initial_admin?.email, 'Email')}>
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                </Button>
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 flex items-center gap-1">
                    <KeyRound className="w-3 h-3" /> One-time temporary password
                  </div>
                  <div data-testid="sa-result-temp-password" className="font-mono text-lg font-bold text-emerald-800 tracking-wide select-all">
                    {result.temp_password}
                  </div>
                </div>
                <Button size="sm" onClick={() => copy(result.temp_password, 'Temp password')} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                </Button>
              </div>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Login URL</div>
                  <div className="font-mono text-xs text-slate-700">{window.location.origin}/login</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => copy(`${window.location.origin}/login`, 'Login URL')}>
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                </Button>
              </div>
            </div>

            <div className="p-3 rounded-lg bg-white border border-slate-200 text-xs text-slate-700">
              <strong>Next steps for the client admin:</strong>
              <ol className="list-decimal ml-4 mt-1 space-y-0.5">
                <li>Open the Login URL above and sign in with the email + temp password.</li>
                <li>They&apos;ll be forced to set a new permanent password.</li>
                <li>From the Users page they can then invite the rest of their team.</li>
              </ol>
            </div>

            <div className="flex flex-wrap gap-2 justify-end pt-2">
              <Button
                data-testid="sa-onboard-another"
                variant="outline"
                onClick={() => {
                  setResult(null);
                  setForm({
                    name: '', address: '', location: '', industry: 'real_estate',
                    contact_person_name: '', contact_email: '', contact_phone: '',
                    plan: 'standard', currency: 'AED', currency_symbol: 'AED',
                    admin_name: '', admin_email: '', admin_phone: '', admin_role: 'FULL_ACCESS',
                  });
                  setStep(1);
                }}
              >
                Onboard another
              </Button>
              <Button data-testid="sa-goto-dashboard" onClick={() => navigate('/superadmin')} className="bg-teal-600 hover:bg-teal-700 text-white">
                Go to Super Admin Dashboard
              </Button>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
}

